import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle } from "lucide-react";
import { formatCurrency, MONTHS } from "@/lib/config";
// 🆕 [Phase 64] Helpers badge "Depuis X jours" (extraits pour testabilité Jest)
// 🆕 [Phase 92 — 2026-07-08] Helpers historique unifié envois
import {
  getJoursDepuisEnvoi, getBadgeRetardClasses, formatBadgeRetardText,
  getJoursDepuisPremierEnvoi, formatResumeEnvois, formatApercuEnvois,
} from "./paiementsBadge";

export function SectionPaiements({
  deptView, deptPaiementsTotal, deptPaiementsNb,
  paiementsAttente, filteredPaiements,
  month, year, navigate, MONTHS: MoisNames,
}) {
  // 🆕 [BONUS 6 — Phase 92] Toggle affichage HT / TTC (par défaut TTC = comportement historique)
  const [modeTaxes, setModeTaxes] = useState('ttc'); // 'ttc' | 'ht'

  // 🎨 [Phase v13.54 — 2026-07-21] Bug fix : le total affiché en haut de la section
  // ne suivait pas le toggle HT/TTC (il utilisait `deptPaiementsTotal` figé, calculé en TTC par le parent).
  // On recalcule le total localement en fonction de `modeTaxes` + `filteredPaiements`, exactement
  // comme la formule L132 utilisée sur les lignes individuelles → source de vérité unique.
  // La prop `deptPaiementsTotal` reste consommée en fallback pour `deptView === 'LMDS'` (inchangé).
  const totalAffiche = useMemo(() => {
    if (!Array.isArray(filteredPaiements) || filteredPaiements.length === 0) {
      return deptPaiementsTotal || 0;
    }
    return filteredPaiements.reduce((sum, f) => {
      const solde = modeTaxes === 'ht' ? (f.solde_du_ht ?? f.solde_du) : f.solde_du;
      return sum + (Number(solde) || 0);
    }, 0);
  }, [filteredPaiements, modeTaxes, deptPaiementsTotal]);
  if (deptView === 'LMDS') {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />
            Paiements en attente du Marche du Store
          </CardTitle>
          <div className="flex gap-4 mt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex-1">
              <p className="text-xs text-amber-600">Total HT en attente</p>
              <p className="text-lg font-bold text-amber-800">{formatCurrency(paiementsAttente?.total_lmds_ht || 0)}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 flex-1">
              <p className="text-xs text-purple-600">CA entreprise en attente</p>
              <p className="text-lg font-bold text-purple-800">{formatCurrency(paiementsAttente?.total_lmds_entreprise || 0)}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(paiementsAttente?.lmds_rapports || []).length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Rapports hebdomadaires non payes</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {[...(paiementsAttente?.lmds_rapports || [])].sort((a, b) => (a.semaine || 0) - (b.semaine || 0)).map((r, i) => (
                  <div key={r.id || r._id || i} className="flex items-center justify-between p-2 border rounded-lg bg-gray-50/50 text-sm cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-colors"
                    onClick={() => navigate(`/sous-traitants/lmds/${r.installateur_id}`)}>
                    <div>
                      <p className="font-medium text-blue-700">S.{r.semaine} — {r.installateur_nom}</p>
                      <p className="text-xs text-slate-400">{r.date_debut} - {r.date_fin}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{formatCurrency(r.total_ht)}</p>
                      <p className="text-xs text-purple-600">Entr: {formatCurrency(r.part_entreprise)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(paiementsAttente?.lmds_factures || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Factures mensuelles non payees</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(paiementsAttente?.lmds_factures || []).map((f, i) => (
                  <div key={f.id || f._id || i} className="flex items-center justify-between p-2 border rounded-lg bg-gray-50/50 text-sm cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-colors"
                    onClick={() => navigate('/sous-traitants/lmds-facturation')}>
                    <div>
                      <p className="font-medium text-blue-700">{f.installateur_nom} — {f.periode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{formatCurrency(f.total_facture)}</p>
                      <p className="text-xs text-purple-600">Entr: {formatCurrency(f.part_entreprise)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!(paiementsAttente?.lmds_rapports?.length) && !(paiementsAttente?.lmds_factures?.length) && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3 border border-green-200">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium text-sm">Tous les rapports et factures sont payes !</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-600" />
          Paiements en attente {deptView !== 'global' ? deptView : ''}
        </CardTitle>
        <CardDescription className="flex items-center justify-between gap-2 flex-wrap">
          <span>
            {deptPaiementsNb} facture(s) — Total :{' '}
            <strong className="text-amber-700" data-testid="paiements-total">
              {formatCurrency(totalAffiche)}
            </strong>{' '}
            <span className="text-xs text-gray-500 font-normal" data-testid="paiements-total-mode">
              ({modeTaxes === 'ttc' ? 'TTC incl. TPS + TVQ' : 'HT avant taxes'})
            </span>
          </span>
          {/* 🆕 [BONUS 6 — Phase 92] Toggle HT / TTC pour lever l'ambiguïté */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setModeTaxes(m => m === 'ttc' ? 'ht' : 'ttc'); }}
            className="text-xs px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-medium transition"
            data-testid="btn-toggle-ht-ttc"
          >
            🔄 {modeTaxes === 'ttc' ? 'Voir HT' : 'Voir TTC'}
          </button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!filteredPaiements?.length ? (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3 border border-green-200">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium text-sm">Toutes les factures sont payees !</span>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredPaiements.map(f => {
              // 🆕 [Phase 92] Compte à partir du PREMIER envoi (pas dernier) — fix bug reset compteur
              const premierEnvoi = f.premier_envoi_date || f.date_envoi_email;
              const joursDepuisEnvoi = getJoursDepuisPremierEnvoi(premierEnvoi, f.date_envoi_email);
              const badgeClasses = getBadgeRetardClasses(joursDepuisEnvoi);
              // 🆕 [BONUS 5+6] Solde à afficher selon toggle (par défaut TTC = solde_du)
              const soldeAffiche = modeTaxes === 'ht' ? (f.solde_du_ht ?? f.solde_du) : f.solde_du;
              // 🆕 [BONUS 5] Résumé envois multi-canal
              const nbEnvoisTotal = (f.nb_envois_total ?? (f.nb_envois_email || 0) + (f.nb_envois_sms || 0));
              const resumeEnvois = formatResumeEnvois(nbEnvoisTotal, f.dernier_envoi_date);
              const apercuEnvois = formatApercuEnvois(f.historique_envois_recents);
              return (
              <div key={f.facture_id}
                onClick={() => f.dossier_id && navigate(`/crm/projets/${f.dossier_id}?tab=factures`)}
                className="flex items-start justify-between gap-3 p-2.5 border rounded-lg bg-gray-50/50 text-sm cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-colors"
                data-testid={`paiement-attente-${f.facture_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate text-blue-700 hover:underline">{f.nom_dossier || f.numero_facture}</p>
                  <p className="text-xs text-gray-500">{f.client_nom || '—'} · {f.type_facture === 'depot' ? 'Depot' : 'Finale'} · {f.departement}</p>
                  {/* 🆕 [BONUS 5 — Phase 92] Aperçu envois multi-canal (email + SMS) sous la facture */}
                  {(resumeEnvois || apercuEnvois) && (
                    <p className="text-xs text-gray-400 mt-0.5" data-testid={`envois-resume-${f.facture_id}`}>
                      {resumeEnvois}{apercuEnvois ? ` — ${apercuEnvois}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-1">
                  <p className="text-sm font-bold text-red-600" data-testid={`solde-${f.facture_id}`}>
                    {formatCurrency(soldeAffiche)}
                    <span className="ml-1 text-xs text-gray-400 font-normal">
                      {modeTaxes === 'ttc' ? 'TTC' : 'HT'}
                    </span>
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <Badge className={`text-xs ${f.statut_paiement === 'partiel' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} border-0`}>
                      {f.statut_paiement === 'partiel' ? 'Partiel' : 'Impaye'}
                    </Badge>
                    {badgeClasses && (
                      <Badge
                        className={`text-xs ${badgeClasses} border`}
                        data-testid={`badge-retard-${f.facture_id}`}
                      >
                        {formatBadgeRetardText(joursDepuisEnvoi)}
                        {nbEnvoisTotal > 1 && ` · ${nbEnvoisTotal}×`}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
